import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from './index';

export interface Applicant {
  id: number;
  user_id: number;
  first_name: string;
  middle_name?: string;
  last_name: string;
  email: string;
  phone: string;
  nin: string;
  nin_status: 'PENDING' | 'VERIFIED' | 'FAILED';
  dob: string;
  nationality: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code?: string;
  country: string;
  employer?: string;
  created_at: string;
  updated_at: string;
}

interface ApplicantUpdateRequest {
  first_name: string;
  middle_name?: string;
  last_name: string;
  phone: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code?: string;
  country: string;
  employer?: string;
}

export const applicantApi = createApi({
  reducerPath: 'applicantApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL}/applicants`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Applicant'],
  endpoints: (builder) => ({
    // Self-service endpoints for APPLICANT role
    getMyProfile: builder.query<Applicant, void>({
      query: () => '/me',
      providesTags: ['Applicant'],
    }),
    
    updateMyProfile: builder.mutation<Applicant, ApplicantUpdateRequest>({
      query: (profileData) => ({
        url: '/me',
        method: 'PUT',
        body: profileData,
      }),
      invalidatesTags: ['Applicant'],
    }),
    
    // Administrative endpoints for OPS/ADMIN roles
    getApplicants: builder.query<Applicant[], { skip?: number; limit?: number; search?: string }>({
      query: (params) => ({
        url: '',
        params,
      }),
      providesTags: ['Applicant'],
    }),
    
    getApplicantById: builder.query<Applicant, number>({
      query: (id) => `/${id}`,
      providesTags: ['Applicant'],
    }),
    
    updateApplicant: builder.mutation<Applicant, { id: number; data: Partial<Applicant> }>({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Applicant'],
    }),
    
    deleteApplicant: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Applicant'],
    }),
  }),
});

export const {
  useGetMyProfileQuery,
  useUpdateMyProfileMutation,
  useGetApplicantsQuery,
  useGetApplicantByIdQuery,
  useUpdateApplicantMutation,
  useDeleteApplicantMutation,
} = applicantApi;